import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Users, TrendingUp, ExternalLink, Download, ArrowUpLeft } from "lucide-react";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);
ChartJS.defaults.font.family = "yekan, Vazir, sans-serif";

/* ================================================================
 *  Helpers
 * ================================================================ */
const toPersianNumber = (num) =>
  String(num).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

export default function TopBotUsersChart({ topUsersData }) {
  const { t } = useTranslation(["user"]);

  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  /* ---------- responsive ---------- */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ---------- داده ---------- */
  const sortedData = useMemo(
    () => (Array.isArray(topUsersData) ? [...topUsersData].sort((a, b) => b.count - a.count) : []),
    [topUsersData]
  );

  const labels = useMemo(
    () =>
      sortedData.map((item) =>
        `${toPersianNumber(String(item.countryCode).replace("+", ""))}${toPersianNumber(item.phone)}`
      ),
    [sortedData]
  );

  const values = useMemo(() => sortedData.map((i) => i.count), [sortedData]);

  /* ---------- دانلود اکسل ---------- */
  const downloadTopUsersExcel = () => {
    if (!topUsersData?.length) return;
    const ws = XLSX.utils.json_to_sheet(
      topUsersData.map((item, index) => ({
        [t("topUsersChart.excel.columnRow")]: index + 1,
        [t("topUsersChart.excel.columnPhone")]: `${item.countryCode}${item.phone}`,
        [t("topUsersChart.excel.columnMessageCount")]: item.count,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("topUsersChart.excel.sheetName"));
    XLSX.writeFile(wb, t("topUsersChart.excel.fileName"));
  };

  /* ================================================================
   *  Empty / Loading
   * ================================================================ */
  if (!topUsersData || topUsersData.length === 0) {
    return (
      <div
        className="
          relative w-full h-[500px] overflow-hidden
          rounded-2xl
          bg-gradient-to-br from-slate-100 via-teal-50 to-teal-100/70
          dark:!bg-gradient-to-br dark:!from-slate-900 dark:!via-[#0B2E2F] dark:!to-slate-900
          backdrop-blur-xl
          border border-teal-500/20 dark:!border-teal-400/15
          shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)]
          dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
          flex flex-col items-center justify-center gap-3
        "
      >
        <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
        <div className="h-14 w-14 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
          <Users size={22} className="animate-pulse text-teal-400/70" />
        </div>
        <p className="text-sm animate-pulse text-slate-500 dark:!text-slate-400">
          {topUsersData && topUsersData.length === 0
            ? t("topUsersChart.noData")
            : t("topUsersChart.loading")}
        </p>
      </div>
    );
  }

  /* ================================================================
   *  Chart.js config
   * ================================================================ */
  const data = {
    labels,
    datasets: [
      {
        label: t("topUsersChart.datasetLabel"),
        data: values,
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return "rgba(20,184,166,0.7)";
          const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(251,191,36,0.95)");
          gradient.addColorStop(0.5, "rgba(20,184,166,0.85)");
          gradient.addColorStop(1, "rgba(13,148,136,0.65)");
          return gradient;
        },
        hoverBackgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return "rgba(251,191,36,0.95)";
          const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(252,211,77,1)");
          gradient.addColorStop(0.5, "rgba(45,212,191,1)");
          gradient.addColorStop(1, "rgba(20,184,166,0.9)");
          return gradient;
        },
        borderRadius: 8,
        borderSkipped: false,
        maxBarThickness: isMobile ? 28 : 48,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      zoom: {
        pan: { enabled: true, mode: "x" },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "x",
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        titleColor: "#FCD34D",
        bodyColor: "#5EEAD4",
        titleFont: { family: "yekan, Vazir, sans-serif", size: 13, weight: "700" },
        bodyFont: { family: "yekan, Vazir, sans-serif", size: 13, weight: "700" },
        padding: 12,
        cornerRadius: 12,
        borderColor: "rgba(251, 191, 36, 0.4)",
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => `📱 ${tooltipItems[0].label}`,
          label: (tooltipItem) =>
            t("topUsersChart.tooltip.messageCount", {
              count: toPersianNumber(tooltipItem.formattedValue),
            }),
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        title: {
          display: !isMobile,
          text: t("topUsersChart.xAxisTitle"),
          color: "#64748B",
          font: { size: 12, weight: "600", family: "yekan, Vazir, sans-serif" },
          padding: { top: 8 },
        },
        ticks: {
          color: "#64748B",
          font: { size: 11, family: "yekan, Vazir, sans-serif" },
          maxRotation: 60,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: isMobile ? 5 : 12,
        },
        grid: { display: false },
        border: { color: "rgba(20, 184, 166, 0.15)" },
      },
      y: {
        title: {
          display: !isMobile,
          text: t("topUsersChart.yAxisTitle"),
          color: "#64748B",
          font: { size: 12, weight: "600", family: "yekan, Vazir, sans-serif" },
          padding: { bottom: 8 },
        },
        ticks: {
          callback: (value) => toPersianNumber(value),
          color: "#64748B",
          font: { size: 11, family: "yekan, Vazir, sans-serif" },
          padding: 6,
        },
        grid: {
          color: "rgba(20, 184, 166, 0.12)",
          drawTicks: false,
        },
        border: { display: false },
      },
    },
  };

  /* ================================================================
   *  RENDER
   * ================================================================ */
  return (
    <div
      className="
        relative w-full h-[500px] overflow-hidden
        rounded-2xl
        bg-gradient-to-br from-slate-100 via-teal-50 to-teal-100/70
        dark:!bg-gradient-to-br dark:!from-slate-900 dark:!via-[#0B2E2F] dark:!to-slate-900
        backdrop-blur-xl
        border border-teal-500/20 dark:!border-teal-400/15
        shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)]
        dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
        transition-colors duration-500
        flex flex-col
      "
    >
      {/* خط تزئینی طلایی بالا */}
      <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />

      {/* گلوی طلایی */}
      <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/15 blur-3xl dark:!bg-amber-400/10" />

      {/* گلوی teal */}
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/15 blur-3xl dark:!bg-teal-500/10" />

      {/* Header */}
      <div className="relative flex items-center justify-between gap-3 p-4 sm:p-5 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="
              shrink-0 flex h-8 w-8 items-center justify-center rounded-lg
              bg-gradient-to-br from-amber-300/30 to-teal-400/20
              border border-amber-300/40
            "
          >
            <Users size={15} className="text-amber-400" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 dark:!text-amber-100 truncate">
              {t("topUsersChart.title")}
            </h3>
            <p className="text-[11px] text-slate-500 dark:!text-slate-400 truncate">
              {t("topUsersChart.subtitle")}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">

          {/* Download */}
          <div className="relative group">
            <button
              type="button"
              onClick={downloadTopUsersExcel}
              disabled={!topUsersData?.length}
              aria-label={t("topUsersChart.actions.download")}
              className="
    group flex h-9 w-9 items-center justify-center
    rounded-xl
    border border-teal-500/20
    bg-white/40
    text-teal-600
    backdrop-blur-md
    transition-all duration-300
    hover:border-amber-400/40
    hover:bg-amber-400/10
    hover:text-amber-500
    dark:!border-teal-400/20
    dark:!bg-white/5
    dark:!text-teal-300
    dark:hover:!border-amber-400/40
    dark:hover:!bg-amber-400/10
    dark:hover:!text-amber-300
  "
            >
              <Download
                size={16}
                strokeWidth={2}
                className="relative z-10 transition-transform duration-300 group-hover:translate-y-0.5"
              />

              {/* Glow */}
              <span
                className="
          pointer-events-none absolute inset-0
          rounded-xl
          bg-gradient-to-br
          from-amber-300/0 via-transparent to-teal-400/0
          transition-all duration-300
          group-hover:from-amber-300/15
          group-hover:to-teal-400/10
        "
              />
            </button>

            {/* Tooltip */}
            <div
              className="
        pointer-events-none absolute
        top-full left-1/2 z-50
        mt-2 -translate-x-1/2 translate-y-[-4px]
        whitespace-nowrap
        rounded-lg
        border border-amber-300/20
        bg-slate-900/95
        px-2.5 py-1.5
        text-[10px] font-medium text-white
        opacity-0 shadow-lg
        backdrop-blur-md
        transition-all duration-200
        group-hover:translate-y-0
        group-hover:opacity-100
      "
            >
              {t("topUsersChart.actions.download")}
            </div>
          </div>

          {/* View Full Chart */}
          <div className="relative group">
            <button
              type="button"
              onClick={() => navigate("/user/top-users")}
              aria-label={t("topUsersChart.actions.viewFullChart")}
              className="
    group flex h-9 w-9 items-center justify-center
    rounded-xl
    border border-teal-500/20
    bg-white/40
    text-teal-600
    backdrop-blur-md
    transition-all duration-300
    hover:border-amber-400/40
    hover:bg-amber-400/10
    hover:text-amber-500
    dark:!border-teal-400/20
    dark:!bg-white/5
    dark:!text-teal-300
    dark:hover:!border-amber-400/40
    dark:hover:!bg-amber-400/10
    dark:hover:!text-amber-300
  "
            >
              <ArrowUpLeft
                size={18}
                strokeWidth={2}
                className="transition-transform duration-300 group-hover:-translate-x-0.5 group-hover:-translate-y-0.5"
              />

              {/* Glow */}
              <span
                className="
          pointer-events-none absolute inset-0
          rounded-xl
          bg-gradient-to-br
          from-amber-300/0 via-transparent to-teal-400/0
          transition-all duration-300
          group-hover:from-amber-300/15
          group-hover:to-teal-400/10
        "
              />
            </button>

            {/* Tooltip */}
            <div
              className="
        pointer-events-none absolute
        top-full left-1/2 z-50
        mt-2 -translate-x-1/2 translate-y-[-4px]
        whitespace-nowrap
        rounded-lg
        border border-amber-300/20
        bg-slate-900/95
        px-2.5 py-1.5
        text-[10px] font-medium text-white
        opacity-0 shadow-lg
        backdrop-blur-md
        transition-all duration-200
        group-hover:translate-y-0
        group-hover:opacity-100
      "
            >
              {t("topUsersChart.actions.viewFullChart")}
            </div>
          </div>

        </div>
      </div>

      {/* Chart */}
      <div className="relative flex-1 min-h-0 px-2 sm:px-4 pb-3 sm:pb-4">
        <Bar data={data} options={options} />
      </div>
    </div>
  );
}